/**
 * True only once the real Optimizely Web snippet has loaded and exposed its
 * API. Never gate on `window.optimizely` truthiness alone: Optimizely's own
 * snippet loader (and any command pushed before it loads) can leave
 * `window.optimizely` as a plain command-queue array long before the real
 * API — and therefore any experiment decision — is attached.
 *
 * Queueing commands (`window.optimizely.push(...)`) on that array is fine —
 * the snippet processes the queue when it loads. Reading decisions
 * (`window.ABTest*`, `window.optimizely.get(...)`) is only meaningful once
 * this returns true.
 */
export function isOptimizelySdkReady (): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.optimizely &&
    typeof window.optimizely.get === 'function'
  )
}

/** Default max time to wait for the Optimizely snippet before giving up. */
export const OPTIMIZELY_MAX_WAIT_MS = 3000

/**
 * Wait until the Optimizely Web snippet is ready, bounded by a timeout so a
 * page's onMounted flow never blocks indefinitely when the snippet fails to
 * load. Resolves `true` as soon as isOptimizelySdkReady() becomes true, or
 * `false` once maxWaitMs has elapsed — callers should then continue with safe
 * localStorage / control defaults.
 */
export async function waitForOptimizelySdk (
  maxWaitMs = OPTIMIZELY_MAX_WAIT_MS
): Promise<boolean> {
  if (typeof window === 'undefined') {
    return false
  }
  const start = Date.now()
  while (!isOptimizelySdkReady() && Date.now() - start < maxWaitMs) {
    // setTimeout, not requestAnimationFrame: rAF is paused in hidden tabs
    // (e.g. a quotes-results link opened in a background tab), which would
    // stall this loop indefinitely.
    // eslint-disable-next-line no-await-in-loop
    await new Promise(resolve => setTimeout(resolve, 50))
  }
  return isOptimizelySdkReady()
}
